const CATERING_ROOM_ONLY = 'Room Only';

const BOARD_RANK = {
    'Room Only': 0,
    'Breakfast Included': 1,
    'Half Board': 2,
    'Full Board': 3,
    'All Inclusive': 4,
};

const DIRECTION = {
    WORSE: -1,
    SAME: 0,
    BETTER: 1,
};

const REJECTION_REASON = {
    BOARD_DOWNGRADE: 'board_downgrade',
    CANCELLATION_DOWNGRADE: 'cancellation_downgrade',
    BOARD_AND_CANCELLATION_DOWNGRADE: 'board_and_cancellation_downgrade',
};

const RECHECK_TYPE = {
    LAZY: 'lazy',
    UPFRONT: 'upfront',
};

const UNAVAILABLE_REASON = {
    NO_ALTERNATIVE: 'no_alternative',
    ALTERNATIVE_REJECTED: 'alternative_rejected',
};

const PRICE_TOLERANCE = 1;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS = 3;

function getBoardRank(board) {
    const rank = BOARD_RANK[board || CATERING_ROOM_ONLY];
    return typeof rank === 'number' ? rank : BOARD_RANK[CATERING_ROOM_ONLY];
}

function getBoard(deal) {
    return (deal && deal.catering) || CATERING_ROOM_ONLY;
}

function getCancellationDeadlineDayKey(deadline) {
    if (!deadline) {
        return null;
    }
    const date = new Date(deadline);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// A deal only carries chooseprod once it has been rechecked; the deal the user clicked on the
// hotel page has not been, so its policy has to be read from the raw search fields instead.
function getCancellationTerms(deal) {
    const dca = deal?.chooseprod?.dca;
    if (dca) {
        return { non_refundable: !!dca.nonRefundable, deadline: dca.dealIsFullyRefundableTill ?? null };
    }

    const details = deal?.original?.details;
    return {
        non_refundable: !!details?.non_refundable,
        deadline: details?.refundable_until ?? null,
    };
}

function getEffectiveCancellationDeadline(deal, now) {
    const { non_refundable, deadline } = getCancellationTerms(deal);

    if (non_refundable || !deadline) {
        return null;
    }
    const deadline_time = new Date(deadline).getTime();
    if (Number.isNaN(deadline_time)) {
        return null;
    }
    const reference = typeof now === 'number' ? now : Date.now();
    return deadline_time < reference ? null : deadline;
}

function compareBoard(old_board, new_board) {
    const delta = getBoardRank(new_board) - getBoardRank(old_board);
    if (delta === 0) {
        return DIRECTION.SAME;
    }
    return delta > 0 ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function compareCancellation(old_deadline, new_deadline) {
    const old_key = getCancellationDeadlineDayKey(old_deadline);
    const new_key = getCancellationDeadlineDayKey(new_deadline);

    if (old_key === null && new_key === null) {
        return DIRECTION.SAME;
    }
    if (old_key === null) {
        return DIRECTION.BETTER;
    }
    if (new_key === null) {
        return DIRECTION.WORSE;
    }
    if (old_key === new_key) {
        return DIRECTION.SAME;
    }
    return new_key > old_key ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function getCancellationDowngradeInDays(old_deadline, new_deadline) {
    const old_key = getCancellationDeadlineDayKey(old_deadline);
    const new_key = getCancellationDeadlineDayKey(new_deadline);

    if (old_key === null || new_key === null) {
        return null;
    }
    return Math.max(0, (old_key - new_key) / MS_PER_DAY);
}

function comparePrice(old_price, new_price) {
    if (!Number.isFinite(old_price) || !Number.isFinite(new_price)) {
        return DIRECTION.SAME;
    }
    const delta = new_price - old_price;
    if (Math.abs(delta) <= PRICE_TOLERANCE) {
        return DIRECTION.SAME;
    }
    return delta < 0 ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function getRejectionReason(board_direction, cancellation_direction, cancellation_downgrade_days) {
    const board_worse = board_direction === DIRECTION.WORSE;
    const within_tolerance =
        cancellation_downgrade_days !== null && cancellation_downgrade_days <= CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS;
    const cancellation_worse = cancellation_direction === DIRECTION.WORSE && !within_tolerance;

    if (board_worse && cancellation_worse) {
        return REJECTION_REASON.BOARD_AND_CANCELLATION_DOWNGRADE;
    }
    if (board_worse) {
        return REJECTION_REASON.BOARD_DOWNGRADE;
    }
    if (cancellation_worse) {
        return REJECTION_REASON.CANCELLATION_DOWNGRADE;
    }
    return null;
}

function buildRecheckComparison(original_deal, alternative_deal, now) {
    const reference = typeof now === 'number' ? now : Date.now();

    const old_price = original_deal ? original_deal.totalPrice : null;
    const new_price = alternative_deal ? alternative_deal.totalPrice : null;
    const price_direction = comparePrice(old_price, new_price);

    const old_board = getBoard(original_deal);
    const new_board = getBoard(alternative_deal);
    const board_direction = compareBoard(old_board, new_board);

    const old_cancellation_deadline = getEffectiveCancellationDeadline(original_deal, reference);
    const new_cancellation_deadline = getEffectiveCancellationDeadline(alternative_deal, reference);
    const cancellation_direction = compareCancellation(old_cancellation_deadline, new_cancellation_deadline);
    const cancellation_downgrade_days = getCancellationDowngradeInDays(
        old_cancellation_deadline,
        new_cancellation_deadline,
    );

    const rejection_reason = getRejectionReason(board_direction, cancellation_direction, cancellation_downgrade_days);

    return {
        old_price,
        new_price,
        price_changed: price_direction !== DIRECTION.SAME,
        price_direction,
        old_board,
        new_board,
        board_changed: board_direction !== DIRECTION.SAME,
        board_direction,
        old_cancellation_deadline,
        new_cancellation_deadline,
        deadline_changed: cancellation_direction !== DIRECTION.SAME,
        cancellation_direction,
        cancellation_downgrade_days,
        acceptable: !rejection_reason,
        rejection_reason,
    };
}

function getCheapestDeal(deals) {
    return (deals || [])
        .filter((deal) => Number.isFinite(deal?.totalPrice))
        .reduce((cheapest, deal) => (!cheapest || deal.totalPrice < cheapest.totalPrice ? deal : cheapest), null);
}

function pickBestAcceptableAlternative(original_deal, alternative_deals, now) {
    const reference = typeof now === 'number' ? now : Date.now();

    const acceptable = (alternative_deals || [])
        .map((deal) => ({ deal, comparison: buildRecheckComparison(original_deal, deal, reference) }))
        .filter(({ comparison }) => comparison.acceptable);

    const cheapest = getCheapestDeal(acceptable.map(({ deal }) => deal));

    return acceptable.find(({ deal }) => deal === cheapest) || null;
}

// The terms of the deal the user clicked, reportable even when the recheck returned
// nothing to compare it against.
function buildOriginalTerms(original_deal, now) {
    return {
        old_price: original_deal?.totalPrice ?? null,
        old_board: original_deal ? getBoard(original_deal) : null,
        old_cancellation_deadline: getEffectiveCancellationDeadline(original_deal, now),
    };
}

function buildRejectedComparison(original_deal, alternative_deals, now) {
    const cheapest = getCheapestDeal(alternative_deals);
    return cheapest ? buildRecheckComparison(original_deal, cheapest, now) : null;
}

module.exports = {
    CATERING_ROOM_ONLY,
    BOARD_RANK,
    DIRECTION,
    REJECTION_REASON,
    RECHECK_TYPE,
    UNAVAILABLE_REASON,
    getBoardRank,
    getBoard,
    CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS,
    getCancellationDeadlineDayKey,
    getCancellationDowngradeInDays,
    getCancellationTerms,
    getEffectiveCancellationDeadline,
    compareBoard,
    compareCancellation,
    comparePrice,
    buildRecheckComparison,
    buildOriginalTerms,
    getCheapestDeal,
    pickBestAcceptableAlternative,
    buildRejectedComparison,
};
